import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, SecurityContext, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { map, Observable, Subject, take, takeUntil } from 'rxjs';
import { selectConnections } from '../../../core/ngrx/selectors/connection.selectors';
import { v4 as uuidv4 } from 'uuid';
import { addApplicationFields, addComponentFields, addComponentFolderFields, addLocatorFields, addLocatorFieldsMobile, addPageFields, addParameterFields, addTestScriptFields, addTestSuiteFields, addUiObjectFields, addUiObjectFieldsMobile, agentFields, agentFieldsEdit, generateMappingFields, mobileScriptFields, releaseFields, releaseFieldsEdit, webScriptFields } from '../../../core/dummy/dummy-data';
import { AIStreamingRequest, ChatMessage, ErrorEvent, MessageEvent, SSEEvent, StatusEvent, ToolCallEvent } from '../../../core/models/sse-events';
import { AgenticAiService } from '../../../core/services/agentic-ai.service';
import { AIStreamingService } from '../../../core/services/ai-streaming.service';
import { TdgService } from '../../../core/services/tdg.service';
import { ToasterService } from '../../../core/services/toaster-service';
import { testCaseVersionMoreDetails } from '../../../core/table-templates/role-based-credentials-table';
import { MessageEnum } from '../../../core/utils/message.enum';
import { AddMissingTestCaseModalComponent } from '../../modal-templates/add-missing-test-case-modal/add-missing-test-case-modal.component';
import { AgentCreateModalComponent } from '../../modal-templates/agent-create-modal/agent-create-modal.component';
import { ApplicationCreateModalComponent } from '../../modal-templates/application-create-modal/application-create-modal.component';
import { CreateDeviceModalComponent } from '../../modal-templates/create-device-modal/create-device-modal.component';
import { StartExecutionModalComponent } from '../../modal-templates/start-execution-modal/start-execution-modal.component';
import { TestPlanModalComponent } from '../../modal-templates/test-plan-modal/test-plan-modal.component';
import { ValidateRecordedActivitiesComponent } from '../../modal-templates/validate-recorded-activities/validate-recorded-activities.component';
import { WebScriptModalComponent } from '../../modal-templates/web-script-modal/web-script-modal.component';
import { GenerateMappingModalComponent } from '../../modal-templates/generate-mapping-modal/generate-mapping-modal.component';
import { RestService } from '../../../core/services/rest.service';
import { MaterialModule } from '../../../core/shared/material.module';
import { ConfigService } from '../../../core/services/config/config.service';
import { DataDisplayModalComponent } from '../../modal-templates/data-display-modal/data-display-modal.component';
import { Card } from 'primeng/card';
import { UploadDefectReportModalComponent } from '../../modal-templates/upload-defect-report-modal/upload-defect-report-modal.component';
import { ExcelExportService } from '../../../core/services/download-excel/excel-export.service';

/**
 * A single entry in a message's workflow log (e.g. one tool_call step).
 * `icon` maps to a file at assets/images/virtusa-icons/agent/{icon}.svg
 * (e.g. 'lightning', 'route', 'processing', 'check-circle').
 */
interface WorkflowStep {
  message: string;
  icon?: string;
}

// Dynamic Card System
enum CardType {
  CREATE_APPLICATION = 'CREATE_APPLICATION_CARD',
  CREATE_PAGE = 'CREATE_PAGE_CARD',
  CREATE_UI_OBJECT = "CREATE_UI_OBJECT_CARD",
  CREATE_UI_LOCATOR = "ADD_NEW_UI_LOCATOR_CARD",
  CREATE_TEST_SUITE = "CREATE_TEST_SUITE_CARD",
  CREATE_TEST_SCRIPT = "CREATE_TEST_SCRIPT_CARD",
  CREATE_REUSABLE_SCRIPT = "CREATE_REUSABLE_SCRIPT_CARD",
  CREATE_REUSABLE_FOLDER = "CREATE_REUSABLE_FOLDER_CARD",
  CREATE_PARAMETER = "CREATE_PARAMETER_CARD",
  CREATE_WEB_TEST_SCRIPT = "CREATE_WEB_AUTOMATION_SCRIPT_CARD",
  CREATE_RELEASE = 'ADD_RELEASE_CARD',
  CREATE_ENGINE = 'CREATE_ENGINE_CARD',
  UPDATE_RELEASE = 'UPDATE_RELEASE_CARD',
  UPDATE_ENGINE = 'UPDATE_ENGINE_CARD',
  CREATE_TEST_PLAN = 'CREATE_TEST_PLAN_CARD',
  UPDATE_TEST_PLAN = 'UPDATE_TEST_PLAN_CARD',
  START_EXECUTION = 'START_EXECUTION_CARD',
  CREATE_DEVICE = 'CONFIGURE_DEVICE_CARD',
  UPADET_DEVICE = 'UPDATE_DEVICE_CARD',
  CREATE_MOBILE_TEST_SCRIPT = "CREATE_MOBILE_AUTOMATION_SCRIPT_CARD",
  CREATE_MOBILE_UI_OBJECT = "CREATE_MOBILE_UI_OBJECT_CARD",
  CREATE_MOBILE_UI_LOCATOR = "ADD_NEW_MOBILE_UI_LOCATOR_CARD",
  ADD_MISSING_TC = "ADD_TEST_CASE_CARD",
  CREATE_API_TEST_SCRIPT = "CREATE_API_AUTOMATION_SCRIPT_CARD",
  CREATE_MAPPING = "ITSR_CREATE_MAPPING_CARD",
  UPDATE_MAPPING_GIT_URL = "UPDATE_IMPACTED_TESTCASE_CARD",
  UPDATE_MOST_IMPACTED_CARD = "UPDATE_MOST_IMPACTED_CARD",
}

interface CardField {
  id: string;
  label: string;
  type: 'text' | 'select' | 'number' | 'textarea';
  placeholder?: string;
  options?: { value: string; label: string }[];
  required?: boolean;
  defaultValue?: any;
}

interface CardConfig {
  type: string;
  title: string;
  description: string;
  buttonText: string;
  buildQuery: (formData: any) => string;
}

@Component({
  selector: 'app-agentic-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, MatProgressSpinnerModule, MaterialModule],
  templateUrl: './agentic-chat.component.html',
  styleUrl: './agentic-chat.component.scss'
})
export class AgenticChatComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('chatHistory') chatHistory!: ElementRef;

  @Output() onEmit = new EventEmitter<any>();

  private _messages: any = [];

  /**
   * Suggestion payload store. Angular's HTML sanitizer strips both data-* and id
   * attributes, so payloads are kept here and looked up via a class-based key
   * (e.g. "isg-1") that survives sanitization.
   */
  private inlineSuggestions = new Map<string, { label: string; text: string }>();
  private suggestionCounter = 0;

  private _externalDisabled = false;
  private _internalDisabled = false;

  @Input()
  set isInputDisabled(val: boolean) {
    this._externalDisabled = val;
  }

  get isInputDisabled(): boolean {
    return this._externalDisabled || this._internalDisabled;
  }

  @Input()
  set messages(value: any) {
    this._messages = value;
    // Auto-scroll when messages change
    this.scrollToBottom();
  }

  get messages(): any {
    return this._messages;
  }

  @Input() sendInput = '';
  @Input() projectId: string = '';
  @Input() automationProjectId: string = '';
  @Input() scriptId: string = '';
  @Input() threadId: string = "";
  @Input() viewId: string = '';
  pendingSecrets: Record<string, string> | null = null;
  attachments: any = [];

  // Backend data
  applications: any[] = [];
  pages: any[] = [];
  allPages: any[] = []; // Store all pages for filtering
  uiObjects: any[] = [];
  devices: any[] = [];
  selectedRows: any = [];
  isActive: boolean = false;
  isNewTabActive: boolean = false;
  isDownloadActive: boolean = false;
  selectedApplicationId: any;
  selectedPageId: any;

  // Loading states
  isApplicationsLoading: boolean = false;
  isPagesLoading: boolean = false;
  isUiObjectsLoading: boolean = false;
  isDevicesLoading: boolean = false;

  // Current modal reference for updating fields
  currentModalRef: any = null;

  // Generic Dynamic Card System
  cardFormData: { [key: string]: any } = {};
  CardType = CardType;

  cardConfigs: { [key: string]: CardConfig } = {
    [CardType.CREATE_APPLICATION]: {
      type: CardType.CREATE_APPLICATION,
      title: 'Create a new application',
      description: 'Click "Create Now" and provide the required details to create a new application',
      buttonText: 'Create Now',
      buildQuery: (data) => {
        const appTypeInfo = this.parseApplicationType(data.applicationType);

        let query = `Create an application with \nname: ${data.applicationName}, \ntype: ${appTypeInfo.applicationType}`;

        // If it's a mobile application type
        if (data.applicationType?.startsWith('MOBILE-')) {
          // Add mobile app type separately
          if (appTypeInfo.mobileAppType) {
            query += `, \nmobileAppType: ${appTypeInfo.mobileAppType}`;
          }

          // Add platform if available
          if (data.platform) {
            query += `, \nmobilePlatform: ${data.platform}`;
          }

          // Add file path if available
          if (data.mobileFileName) {
            this.attachments.push(data.mobileFileName)
            query += `, \nmobileFileName: ${data.mobileFileName?.name}`;
          }

          if (data.browser) {
            query += `, \nbrowser: ${data.browser}`;
          }

        } else if (data.applicationType === 'Web') {
          // For web applications, add browser
          if (data.browser) {
            query += `, \nbrowser: ${data.browser}`;
          }
        }

        return query;
      }
    },
    [CardType.CREATE_PAGE]: {
      type: CardType.CREATE_PAGE,
      title: 'Create a new page',
      description: 'Click "Create Now" and provide the required details to create a new page',
      buttonText: 'Create Now',
      buildQuery: (data) => `Create a page with name: ${data.pageName}, \nURL: ${data.pageURL}${data.description ? ', \ndescription: ' + data.description : ''} in Application: ${data.applicationName}`
    },
    [CardType.CREATE_UI_OBJECT]: {
      type: CardType.CREATE_UI_OBJECT,
      title: 'Create a new ui object',
      description: 'Click "Create Now" and provide the required details to create a new ui object',
      buttonText: 'Create Now',
      buildQuery: (data) => {
        let browserOrPlatform: string;
        if (data.browser) {
          browserOrPlatform = `browser: ${data.browser}`;
        } else if (data.platform) {
          browserOrPlatform = `\nplatform: ${data.platform}`;
        } else {
          browserOrPlatform = '';
        }
        return `Create a ui object name: ${data.uiObjectName}, \nui locator: ${data.locator}${browserOrPlatform ? ', ' + browserOrPlatform : ''} in Application: ${data.applicationName}, \nPage: ${data.pageName}`;
      }
    },
    [CardType.CREATE_UI_LOCATOR]: {
      type: CardType.CREATE_UI_LOCATOR,
      title: 'Create a new ui locator',
      description: 'Click "Create Now" and provide the required details to create a new ui locator',
      buttonText: 'Create Now',
      buildQuery: (data) => {
        let browserOrPlatform: string;
        if (data.browser) {
          browserOrPlatform = `browser: ${data.browser}`;
        } else if (data.platform) {
          browserOrPlatform = `platform: ${data.platform}`;
        } else {
          browserOrPlatform = '';
        }
        return `Create a ui locator: ${data.locator}${browserOrPlatform ? ', ' + browserOrPlatform : ''} in Application: ${data.applicationName}, Page: ${data.pageName}`;
      }
    },
    [CardType.CREATE_TEST_SUITE]: {
      type: CardType.CREATE_TEST_SUITE,
      title: 'Create a new test suite',
      description: 'Click "Create Now" and provide the required details to create a new test suite',
      buttonText: 'Create Now',
      buildQuery: (data) => `Create a test suite name: ${data.suiteName}`
    },
    [CardType.CREATE_TEST_SCRIPT]: {
      type: CardType.CREATE_TEST_SCRIPT,
      title: 'Create a new test script',
      description: 'Click "Create Now" and provide the required details to create a new test script',
      buttonText: 'Create Now',
      buildQuery: (data) => {
        let query = `Create a test script name: ${data.scriptName}`;
        if (!data.useCustomTestSuite && data.testSuite) {
          query += `, in test suite: ${data.testSuite}`;
        }
        return query;
      }
    },
    [CardType.CREATE_REUSABLE_FOLDER]: {
      type: CardType.CREATE_REUSABLE_FOLDER,
      title: 'Create a new component folder',
      description: 'Click "Create Now" and provide the required details to create a new component folder',
      buttonText: 'Create Now',
      buildQuery: (data) => `Create a new component folder: ${data.componentFolderName}`
    },
    [CardType.CREATE_REUSABLE_SCRIPT]: {
      type: CardType.CREATE_REUSABLE_SCRIPT,
      title: 'Create a new component script',
      description: 'Click "Create Now" and provide the required details to create a new component script',
      buttonText: 'Create Now',
      buildQuery: (data) => `Create a new component script: ${data.componentScriptName}, in the component folder: ${data.componentFolderName}`
    },
    [CardType.CREATE_PARAMETER]: {
      type: CardType.CREATE_PARAMETER,
      title: 'Add new parameter',
      description: 'Click "Create Now" and provide the required details to add a new parameter',
      buttonText: 'Create Now',
      buildQuery: (data) => {
        let query = `Add a ${data.type} parameter with name: '${data.parameterName}', type: '${data.parameterType}'`;
        if (data.defaultValue) {
          query += `, default value: ${data.defaultValue}`;
        }
        return query;
      }
    },
    [CardType.CREATE_WEB_TEST_SCRIPT]: {
      type: "CardType.CREATE_WEB_TEST_SCRIPT",
      title: "Generate a web test script",
      description: "Click “Generate Now” and provide the required details to generate automatic web script.",
      buttonText: 'Generate Now',
      buildQuery: (data) => `Create a new component script: ${data.componentScriptName}, in the component folder: ${data.componentFolderName}`
    },
    [CardType.CREATE_RELEASE]: {
      type: "CardType.CREATE_RELEASE",
      title: "Create a new release",
      description: "Click “Create Now” and provide the required details to create a new release.",
      buttonText: 'Create Now',
      buildQuery: (data) => {
        const toISO = (val: any) => val ? new Date(val).toISOString().replace('Z', '000') : '';
        return `Create a new release with the following details:\nRelease Name: '${data.name}'\nDescription: '${data.description}'\nStart Date: '${toISO(data.startDate)}'\nRelease Date: '${toISO(data.releaseDate)}'`;
      }
    }
    ,
    [CardType.CREATE_ENGINE]: {
      type: "CardType.CREATE_ENGINE",
      title: "Create a new engine",
      description: "Click “Create Now” and provide the required details to create a new execution engine.",
      buttonText: 'Create Now',
      buildQuery: (data) => {
        const toISO = (val: any) => val ? new Date(val).toISOString().replace('Z', '000') : '';
        return `Create a new execution engine with the following details:\nExecution Engine Name: '${data.name}'\nType: '${data.type}'\nExpiration Date : '${toISO(data.expiration)}'`;
      }
    },
    [CardType.UPDATE_RELEASE]: {
      type: "CardType.UPDATE_RELEASE",
      title: "Update release",
      description: "Click “Update Now” and provide the required details to update the release.",
      buttonText: 'Update Now',
      buildQuery: (data) => {
        const toISO = (val: any) => val ? new Date(val).toISOString().replace('Z', '000') : '';
        return `Update the release ${data.name} with the following details:\nDescription: ${data.description}\nStart Date: ${toISO(data.startDate)}\nRelease Date: ${toISO(data.releaseDate)}`;
      }
    },
    [CardType.UPDATE_ENGINE]: {
      type: "CardType.UPDATE_ENGINE",
      title: "Update engine",
      description: "Click “Update Now” and provide the required details to update the execution engine.",
      buttonText: 'Update Now',
      buildQuery: (data) => {
        const toISO = (val: any) => val ? new Date(val).toISOString().replace('Z', '000') : '';
        return `Update the execution engine ${data.name} with the following details:Type: ${data.type}\nExpiration Date : ${toISO(data.expiration)}`;
      }
    },
    [CardType.CREATE_TEST_PLAN]: {
      type: "CardType.CREATE_TEST_PLAN",
      title: "Create test plan",
      description: "Click “Create Now” and provide the required details to create a new test plan.",
      buttonText: 'Create Now',
      buildQuery: (data) => {
        const toISO = (val: any) => val ? new Date(val).toISOString().replace('Z', '000') : '';
        return `Create a new test plan with the following details:Type: ${data.type}\nExpiration Date : ${toISO(data.expiration)}`;
      }
    },
    [CardType.UPDATE_TEST_PLAN]: {
      type: "CardType.UPDATE_TEST_PLAN",
      title: "Update test plan",
      description: "Click “Update Now” and provide the required details to update the test plan.",
      buttonText: 'Update Now',
      buildQuery: (data) => {
        const toISO = (val: any) => val ? new Date(val).toISOString().replace('Z', '000') : '';
        return `Update the test plan with the following details:Type: ${data.type}\nExpiration Date : ${toISO(data.expiration)}`;
      }
    },
    [CardType.START_EXECUTION]: {
      type: "CardType.START_EXECUTION",
      title: "Start execution",
      description: "Click “Start Now” to begin the execution.",
      buttonText: 'Start Now',
      buildQuery: (data) => {
        const toISO = (val: any) => val ? new Date(val).toISOString().replace('Z', '000') : '';
        return `Update the test plan with the following details:Type: ${data.type}\nExpiration Date : ${toISO(data.expiration)}`;
      }
    },
    [CardType.CREATE_DEVICE]: {
      type: "CardType.CREATE_DEVICE",
      title: "Create device",
      description: "Click “Create Now” and provide the required details to create a new device.",
      buttonText: 'Create Now',
      buildQuery: (data) => {
        const toISO = (val: any) => val ? new Date(val).toISOString().replace('Z', '000') : '';
        return `Create a new device with the following details:Type: ${data.type}\nExpiration Date : ${toISO(data.expiration)}`;
      }
    },
    [CardType.UPADET_DEVICE]: {
      type: "CardType.UPADET_DEVICE",
      title: "Update device",
      description: "Click “Update Now” and provide the required details to update the device.",
      buttonText: 'Update Now',
      buildQuery: (data) => {
        const toISO = (val: any) => val ? new Date(val).toISOString().replace('Z', '000') : '';
        return `Update the device with the following details:Type: ${data.type}\nExpiration Date : ${toISO(data.expiration)}`;
      }
    },
    [CardType.CREATE_MOBILE_TEST_SCRIPT]: {
      type: "CardType.CREATE_MOBILE_TEST_SCRIPT",
      title: "Generate a mobile test script",
      description: "Click “Generate Now” and provide the required details to generate automatic mobile script.",
      buttonText: 'Generate Now',
      buildQuery: (data) => `Generate automation mobile script steps for:\nDevice: ${data?.deviceName}`
    },
    [CardType.ADD_MISSING_TC]: {
      type: "CardType.ADD_MISSING_TC",
      title: "Add missing test case",
      description: "Click “Add Now” and provide the required details to add a missing test case.",
      buttonText: 'Add Now',
      buildQuery: (data) => `Add a missing test case with the following details:\nDevice: ${data?.deviceName}`
    },
    [CardType.CREATE_MOBILE_UI_OBJECT]: {
      type: CardType.CREATE_MOBILE_UI_OBJECT,
      title: 'Create a new ui object',
      description: 'Click "Create Now" and provide the required details to create a new ui object',
      buttonText: 'Create Now',
      buildQuery: (data) => `Create a ui object name: ${data.uiObjectName}, ui locator: ${data.locator}, platform: ${data.platform} in Application: ${data.applicationName}, Page: ${data.pageName}`
    },
    [CardType.CREATE_MOBILE_UI_LOCATOR]: {
      type: CardType.CREATE_MOBILE_UI_LOCATOR,
      title: 'Create a new ui locator',
      description: 'Click "Create Now" and provide the required details to create a new ui locator',
      buttonText: 'Create Now',
      buildQuery: (data) => `Create a ui locator: ${data.locator}, platform: ${data.platform} in Application: ${data.applicationName}, Page: ${data.pageName}`
    },
    [CardType.CREATE_API_TEST_SCRIPT]: {
      type: "CardType.CREATE_API_TEST_SCRIPT",
      title: "Generate API test script",
      description: "Click “Generate Now” and provide the required details to generate API script.",
      buttonText: 'Generate Now',
      buildQuery: (data) => {
        let query = `Generate automation API script steps for:\nMappedTestCase: ${data?.mappedTestCase}`;
        if (data.file) {
          this.attachments.push(data.file)
          // query += `, \nSwagger File Name: ${data.file?.name}`;
        }
        return query;
      }
    },
    [CardType.CREATE_MAPPING]: {
      type: "CardType.GENERATE_MAPPING",
      title: "Create new test case - code mapping",
      description: "Click “Create Now” and provide the required details to create new test case - code mapping",
      buttonText: 'Create Now',
      buildQuery: (data) => {
        let query = `Create a new mapping with below details:\nMapping Name: ${data?.mappingName} \nVector Database: ${data?.vectorDb} \nGitHub URL: ${data?.githubRepoLink} \nConnection Name: ${data?.llm}`;
        if (data.file) {
          this.attachments.push(data.file)
        }
        if (data.githubToken) {
          //attach password
          this.pendingSecrets = {
            "gitHubToken": data.githubToken,
            "vectorDatabaseKey": data?.apiKey
          }
        }
        return query;
      },
    },
    [CardType.UPDATE_MAPPING_GIT_URL]: {
      type: "CardType.UPDATE_MAPPING_GIT_URL",
      title: "Update GitHub repository link",
      description: "Click “Update Now” and provide the new GitHub repository link.",
      buttonText: 'Update Now',
      buildQuery: (data) => `Update the mapping Git URL with the following details:\nMapping Name: ${data?.mappingName} \nGitHub URL: ${data?.githubRepoLink}`
    },
    [CardType.UPDATE_MOST_IMPACTED_CARD]: {
      type: "CardType.UPDATE_MOST_IMPACTED_CARD",
      title: "Upload defect report",
      description: "To generate the most impacted test cases, please provide the defect report.",
      buttonText: 'Upload Now',
      buildQuery: (data) => `Identify most impacted Test Cases for`
    }

  };

  // messages: ChatMessage[] = [];
  userQuery = '';
  isStreaming = false;
  currentStatus: StatusEvent | null = null;
  errorMessage: string | null = null;
  currentSuggestions: any[] = [];
  showSuggestions = false;
  private readonly modalService = inject(NgbModal);
  private readonly configService = inject(ConfigService);

  // Configuration
  // threadId = uuidv4();

  // Private
  private destroy$ = new Subject<void>();
  // reference to the global keydown handler so we can remove it on destroy
  private keydownHandler: any = null;
  // Track the start index of current streaming session to only replace messages within this session
  private currentStreamSessionStartIndex: number = -1;

  // LLM connections cache
  llmConnections: any[] = [];
  isLLMConnectionsLoading = false;

  /**
   * Parse combined application type value into separate applicationType and mobileAppType
   * @param combinedValue - Format: "WEB" or "MOBILE-NATIVE", "MOBILE-HYBRID", etc.
   * @returns Object with applicationType and mobileAppType (if applicable)
   */
  parseApplicationType(combinedValue: string): { applicationType: string; mobileAppType?: string } {
    if (!combinedValue) {
      return { applicationType: '' };
    }

    if (combinedValue === 'Web') {
      return { applicationType: 'WEB' };
    }

    // For MOBILE-* values, extract the mobile app type
    if (combinedValue.startsWith('MOBILE-')) {
      const mobileType = combinedValue.substring(7); // Remove 'MOBILE-' prefix
      return {
        applicationType: 'MOBILE',
        mobileAppType: mobileType
      };
    }

    // Fallback
    return { applicationType: combinedValue };
  }

  /**
   * Combine applicationType and mobileAppType back into UI format
   * @param applicationType - "WEB" or "MOBILE"
   * @param mobileAppType - "NATIVE", "HYBRID", "MOBILE_WEB", "CROSS_PLATFORM"
   * @returns Combined value for UI dropdown
   */
  combineApplicationType(applicationType: string, mobileAppType?: string): string {
    if (applicationType === 'WEB') {
      return 'WEB';
    }

    if (applicationType === 'MOBILE' && mobileAppType) {
      return `MOBILE-${mobileAppType}`;
    }

    return applicationType;
  }

  constructor(
    private readonly aiStreamingService: AIStreamingService,
    private readonly sanitizer: DomSanitizer,
    public toaster: ToasterService,
    private readonly agenticAiService: AgenticAiService,
    private readonly tdgService: TdgService,
    private readonly restService: RestService,
    private readonly store: Store,
    private excelService: ExcelExportService,
  ) { }

  /**
   * Show copy icon only when the AI message is finalized (not streaming).
   */
  shouldShowCopy(msg: any, index: number): boolean {
    if (msg?.role !== 'ai' || !msg?.content) {
      return false;
    }

    // Hide copy icon for AI messages that belong to the current streaming session
    if (this.isStreaming && index >= this.currentStreamSessionStartIndex) {
      return false;
    }

    return true;
  }

  /**
   * Angular lifecycle hook - OnInit
   * - Subscribes to streaming events
   * - Restores or initializes thread id and last chat state
   * - Registers a global keydown handler to focus the input textarea
   */
  ngOnInit(): void {
    this.subscribeToStreamEvents();
    this.threadId = this.threadId || uuidv4();
    localStorage.setItem('t_id', this.threadId);
    if (this.aiStreamingService.getLastChat()?.threadId) {
      this.threadId = this.aiStreamingService.getLastChat().threadId;
      this.messages = this.aiStreamingService.getLastChat().messages;
    }
    this.messages?.forEach((msg: any) => {
      // Only AI responses go through the markdown→HTML pipeline.
      // Human messages are stored and displayed as plain text to prevent XSS.
      if (msg.role === 'ai') {
        msg.content = this.parseMarkdown(msg.content);
      }
    });


    // Add a global keydown listener to focus the textarea when typing,
    // but skip common copy/paste/cut shortcuts (Ctrl/Cmd + C/V/X)
    // this.keydownHandler = (event: KeyboardEvent) => {
    //   const myTextarea = document.getElementById('sendTxtArea') as HTMLElement | null;
    //   if (!myTextarea) return;

    // const key = (event.key || '').toLowerCase();
    // Skip focusing when user is performing common shortcuts (copy/paste/cut)
    // if ((event.ctrlKey || event.metaKey) && ['c', 'v', 'x'].includes(key)) return;
    // if (event.ctrlKey) return;

    // if (document.activeElement !== myTextarea) myTextarea.focus();
    // };

    // document.addEventListener('keydown', this.keydownHandler);
  }

  ngAfterViewInit(): void {
    /**
     * Angular lifecycle hook - AfterViewInit
     * Ensures the chat view is scrolled to bottom after the view initializes
     * and triggers a click on the first text wrapper to ensure focus/UI state.
     */
    // Scroll to bottom on initialization
    this.scrollToBottom();
    let doc: any = document.getElementsByClassName('txt-wrapper')[0];
    doc?.click();
  }

  send() {
    /**
     * Trigger sending the current input if present.
     * No return value. Delegates to `sendQuery()` which handles streaming.
     */
    if (this.isSendDisabledByAttachment) return;
    if (this.sendInput || this.attachments.length) {
      this.sendQuery();
    }
  }

  get isSendDisabledByAttachment(): boolean {
    return this.attachments.length > 0 && !this.sendInput?.trim();
  }

  onSuggestionClick(suggestion: any): void {
    /**
     * Handle suggestion click - send the suggestion text and hide suggestions
     */
    this.sendInput = suggestion.text;
    this.showSuggestions = false;
    this.currentSuggestions = [];
    this.sendQuery();
  }

  handleInlineSuggestionClick(event: MouseEvent | KeyboardEvent): void {
    /**
     * Handle click on inline suggestion elements
     */
    if (this.isInputDisabled) return;

    const target = event.target as HTMLElement;
    const suggestionElement = target.closest('.inline-suggestion');

    if (suggestionElement) {
      // Angular's HTML sanitizer removes both data-* and id attributes.
      // We encode the Map key as a CSS class ("isg-N") which IS preserved.
      const keyClass = Array.from((suggestionElement as HTMLElement).classList)
        .find((c) => c.startsWith('isg-'));
      const suggestion = keyClass ? this.inlineSuggestions.get(keyClass) : undefined;
      if (suggestion) {
        this.sendInput = suggestion.text;
        this.sendQuery();
      }
    }
  }

  triggerFileInput() {
    /**
     * Opens the native file picker by programmatically clicking the hidden
     * file input element.
     */
    this.fileInput.nativeElement.click();
  }

  // File selected handler
  onFileSelected(event: any) {
    /**
     * Handle file input selection and store a preview for images.
     */
    const file: File | undefined = event?.target?.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      // const data: any = file;
      // data.imageUrl = reader.result;
      this.attachments.push(file);
    };
    reader.readAsDataURL(file);
  }

  isImage(file: any) {
    // Return true when the file MIME type indicates an image
    return Boolean(file?.type?.includes('image'));
  }

  /**
   * Subscribe to all streaming events
   */
  private subscribeToStreamEvents(): void {
    this.aiStreamingService
      .getStatusEvents$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: StatusEvent) => {
        this.handleStatusEvent(event);
      });

    this.aiStreamingService
      .getMessageEvents$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: MessageEvent) => {
        this.handleMessageEvent(event);
      });

    this.aiStreamingService
      .getToolCallEvents$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: ToolCallEvent) => {
        // this.handleToolCallEvent(event);
        // this.handleStatusEvent(event);
      });

    this.aiStreamingService
      .getErrorEvents$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: ErrorEvent) => {
        this.handleErrorEvent(event);
      });
  }

  /**
   * Send user query to AI service
   */
  sendQuery(): void {
    const userMessage: ChatMessage = {
      id: uuidv4(),
      role: 'human',
      // Store user input as plain text — never parse through innerHTML to prevent XSS.
      content: this.sendInput,
      timestamp: new Date().toISOString(),
      isSender: true,
      files: [...this.attachments]
    };
    if (this.selectedRows.length) {
      const selectedText = this.selectedRows.map((ele: any) => ele.chipValue).join(', ');
      let combined: string;
      if (this.viewId === 'test_case_table') {
        combined = `${this.sendInput},\nSelected Test cases: ${selectedText}`;
      } else {
        combined = this.sendInput + (selectedText ? ` ${selectedText}` : '');
      }
      userMessage.content = combined; // plain text only
      this.sendInput = combined;
      this.onEmit.emit({ type: 'clearRowSelection' });
      this.selectedRows = [];
    }

    this.messages.push(userMessage);

    // Immediately add a processing message – starts in "Thinking..." state.
    // workflowSteps accumulates each tool_call message as it arrives.
    const processingMessage = {
      id: uuidv4(),
      role: 'system',
      content: 'Processing your request...',
      timestamp: new Date().toISOString(),
      isProcessing: true,
      isThinking: true,
      workflowSteps: [] as WorkflowStep[],
      workflowExpanded: true
    };
    this.messages.push(processingMessage);

    // Mark the start of new streaming session (next message index after user message)
    this.currentStreamSessionStartIndex = this.messages.length;
    this.isStreaming = true;
    this.errorMessage = null;
    this.scrollToBottom();

    const request: AIStreamingRequest = {
      query: this.sendInput,
      thread_id: this.threadId,
      project_id: this.projectId
    };

    if (this.pendingSecrets) {
      request.service_secrets = JSON.stringify(this.pendingSecrets);
      this.pendingSecrets = null;
    }

    if (this.attachments.length > 0) {
      //console.log("attachments-->>",this.attachments);
      request.files = this.attachments[0];
      this.attachments = [];
    }

    this._internalDisabled = true;
    this.aiStreamingService
      .connectToStream(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (_event: SSEEvent) => {
          // streaming events handled via dedicated observables
        },
        error: (error: any) => {
          this._internalDisabled = false;
          this.currentStreamSessionStartIndex = -1;
          console.error('Stream error:', error);
          this.isStreaming = false;
          this.errorMessage = `Error: ${error.message || 'Unknown error'}`;
        },
        complete: () => {
          this._internalDisabled = false;
          this.isStreaming = false;
          if (this.selectedRows?.length) {
            this.onEmit.emit({ type: 'clearRowSelection' });
            this.selectedRows = [];
          }
          this.updateLastChat();
        },
      });

    setTimeout(() => {
      this.sendInput = '';
    }, 100);
  }

  /**
   * Handle status events
   */
  private handleStatusEvent(event: StatusEvent): void {
    this.currentStatus = event;

    if (this.isStartedOrToolCallEvent(event)) {
      this.handleStreamStarted(event);
    } else if (this.isCompletedOrFailedEvent(event)) {
      this.handleStreamCompleted(event);
    }
  }

  private isStartedOrToolCallEvent(event: StatusEvent): boolean {
    return event.status === 'started' || event.type === 'tool_call';
  }

  private isCompletedOrFailedEvent(event: StatusEvent): boolean {
    return event.status === 'completed' || event.status === 'failed';
  }

  private handleStreamStarted(event: StatusEvent): void {
    this.isStreaming = true;
    this.errorMessage = null;

    const procIdx = this.messages.findIndex((m: any) => m.isProcessing);

    if (procIdx < 0) {
      // Safety net – processing message was not yet added (e.g. event arrived before sendQuery push)
      this.messages.push({
        id: uuidv4(),
        role: 'system',
        content: event.message || 'Processing your request...',
        timestamp: event.timestamp,
        isProcessing: true,
        isThinking: event.status === 'started',
        workflowSteps: event.type === 'tool_call' ? [this.toWorkflowStep(event)] : [],
        workflowExpanded: true
      });
    } else if (event.type === 'tool_call') {
      const msg = this.messages[procIdx];
      msg.isThinking = false;
      this.addWorkflowStepIfNew(msg, this.toWorkflowStep(event));
    }
    // For 'started' events when the message already exists: no change –
    // the message stays in isThinking state until the first tool_call arrives.

    this.scrollToBottom();
  }

  private handleStreamCompleted(event: StatusEvent): void {
    this._internalDisabled = false;
    this.isStreaming = false;
    this.currentStreamSessionStartIndex = -1;

    this.markProcessingMessageAsComplete();
    this.addCompletionMessage(event);
    this.handleSuggestions(event);
    this.emitToolData(event);
    this.updateLastChat();
  }

  private markProcessingMessageAsComplete(): void {
    const processingMessageIndex = this.messages.findIndex(
      (msg: any) => msg.role === 'system' && msg.isProcessing === true
    );

    if (processingMessageIndex > -1) {
      this.messages[processingMessageIndex].isProcessing = false;
      this.messages[processingMessageIndex].isThinking = false;
      // Collapse the workflow steps panel once streaming is done.
      this.messages[processingMessageIndex].workflowExpanded = false;
    }
  }

  toggleWorkflow(msg: any): void {
    msg.workflowExpanded = !msg.workflowExpanded;
  }

  /**
   * Builds a workflow step entry from a tool_call/status event, carrying the
   * icon name (if any) so the template can render the matching svg from
   * assets/images/virtusa-icons/agent/{icon}.svg
   */
  private toWorkflowStep(event: StatusEvent): WorkflowStep {
    return { message: event.message, icon: event.icon };
  }

  /**
   * Appends a workflow step only if a step with the same message doesn't already
   * exist, preventing duplicate steps from repeated SSE events.
   */
  private addWorkflowStepIfNew(proc: any, step: WorkflowStep): void {
    const existing: WorkflowStep[] = proc.workflowSteps ?? [];
    if (!existing.some((s) => s.message === step.message)) {
      proc.workflowSteps = [...existing, step];
    }
  }

  private addCompletionMessage(event: StatusEvent): void {
    this.messages.push({
      message: event.message,
      timestamp: event.timestamp,
      isProcessing: false,
      status: event.status,
      tool_data: event.tool_data,
      suggestions: event.suggestions,
      keep_in_msg: event.keep_in_msg
    } as any);
  }

  private handleSuggestions(event: StatusEvent): void {
    if (event.suggestions && event.suggestions.length > 0) {
      this.currentSuggestions = event.suggestions;
      this.showSuggestions = true;
    }
  }

  private emitToolData(event: StatusEvent): void {
    if(event.tool_data?.result[0]?.id === 'source_code_mapping_table') {
      event.tool_data.result.forEach((ele: any) => {
        ele.type = 'code_coverage_table';
      });
    }


    if(event.tool_data?.results?.length) {
      event.tool_data.result = event.tool_data.results;
    }
    if (event.tool_data?.result?.length) {
      this.onEmit.emit(event.tool_data);
    } else {
      this.onEmit.emit({ type: 'stream_completed', result: [] });
    }
  }  /**
   * Returns true when a message looks like an intermediate backend routing/status
   * notification (short, single-line, starts with an emoji character).
   * The backend occasionally sends these as `message` events (role: 'ai') instead
   * of `tool_call` events; they must go into workflowSteps, not the chat history.
   */
  private isWorkflowStepContent(content: string): boolean {
    const text = (content ?? '').trim();
    return (
      text.length < 160 &&
      !text.includes('\n') &&
      /^\p{Emoji}/u.test(text)
    );
  }

  /**
   * Handle message events
   */
  private handleMessageEvent(event: MessageEvent): void {
    // Only add AI role messages to chat display
    // Skip 'human' role messages as they're already added in sendQuery()
    // Skip 'tool' role messages as they should be handled separately
    if (event.role === 'ai') {
      // If a workflow is actively streaming AND the AI message content matches the
      // emoji-prefixed short-line pattern used by backend routing/status updates,
      // absorb it into workflowSteps instead of rendering it as a standalone line.
      const procIdx = this.messages.findIndex((m: any) => m.isProcessing);
      // Absorb into workflowSteps when:
      //   a) backend explicitly marks it as a workflow event via `action`, OR
      //   b) content matches the emoji-prefixed short-line heuristic
      if (procIdx >= 0 && (!!event.action || this.isWorkflowStepContent(event.content))) {
        const proc = this.messages[procIdx];
        proc.isThinking = false;
        const step = { message: event.content, icon: event.icon };
        this.addWorkflowStepIfNew(proc, step);
        this.scrollToBottom();
        return;
      }

      // Find the last AI message in current streaming session ONLY (after currentStreamSessionStartIndex)
      const lastAiMessageIndex = this.messages.findLastIndex((msg: any, index: number) =>
        msg.role === 'ai' &&
        !msg.isProcessing &&
        index >= this.currentStreamSessionStartIndex
      );

      if (lastAiMessageIndex !== -1) {
        // Replace the last AI message from current session with the new one
        this.messages[lastAiMessageIndex] = {
          id: this.messages[lastAiMessageIndex].id,
          role: event.role,
          content: this.parseMarkdown(event.content),
          metadata: event.metadata,
          timestamp: event.timestamp,
        };
      } else {
        // If no previous AI message in current session, add as new
        const message: ChatMessage = {
          id: uuidv4(),
          role: event.role,
          content: this.parseMarkdown(event.content),
          metadata: event.metadata,
          timestamp: event.timestamp,
        };
        this.messages.push(message);
      }

      console.log("Messages--->>", this.currentStreamSessionStartIndex, this.messages);
      this.scrollToBottom();
    }
  }

  /**
   * Handle error events
   */
  private handleErrorEvent(event: ErrorEvent): void {
    /**
     * Handle error events coming from the streaming service.
     * - Logs the error, sets a human-readable `errorMessage` and stops streaming.
     */
    console.error(`Error: ${event.message}`);
    this.errorMessage = `${event.error || event.message}`;
    this.messages.push({
      message: this.errorMessage,
      timestamp: event.timestamp,
      isProcessing: false,
      status: event.type,
    } as any);

    this.isStreaming = false;
    // Emit so the parent can stop any loading state
    this.onEmit.emit({ type: 'stream_error', result: [] });
  }

  /**
   * Clear chat history
   */
  clearChat(): void {
    this.messages = [];
    this.inlineSuggestions.clear(); // release stored suggestion payloads
    this.errorMessage = null;
    this.currentStatus = null;
    this.threadId = "";
  }

  /**
   * Stop current stream
   */
  stopStream(): void {
    /**
     * Requests the streaming service to close the current SSE/stream and
     * toggles local `isStreaming` state to false.
     */
    this.aiStreamingService.closeStream();
    this.isStreaming = false;
  }

  /**
   * Scroll chat to bottom
   */
  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.chatHistory?.nativeElement) {
        const element = this.chatHistory.nativeElement;
        element.scrollTo({
          top: element.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 0);
  }

  /**
   * Get message role display text
   */
  getRoleDisplay(role: string): string {
    switch (role) {
      case 'human':
        return 'You';
      case 'ai':
        return 'AI Assistant';
      case 'tool':
        return 'Tool Response';
      case 'system':
        return 'System';
      default:
        return role;
    }
  }

  /**
   * Format timestamp for display
   */
  formatTime(timestamp: string): string {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString();
    } catch {
      return timestamp;
    }
  }

  /**
   * Parse markdown content and convert to HTML
   */
  parseMarkdown(content: string): SafeHtml {
    if (!content) return '';

    // Dynamically check for all card types and replace markers
    Object.values(CardType).forEach(cardType => {
      const marker = `[${cardType}]`;
      if (content.includes(marker)) {
        content = content.replace(marker, `||${cardType}||`);
      }
    });

    // First, convert suggestion lines to clickable elements
    let html = content;
    const lines = content.split('\n');
    let processedLines: string[] = [];

    for (let line of lines) {
      const trimmedLine = line.trim();

      // Check if this line is a suggestion item (A), B), C), etc. or numbered 1), 2), 3))
      // Pattern 1: Handle markdown formatted options like *A), **A), **A) text**, A)
      let suggestionMatch = trimmedLine.match(/^[\*\s]*([A-Z])\)\s*(.+)$/);

      // Pattern 2: Handle numbered options like 1), 2), *1), **1)**
      let numberedMatch = null;
      if (!suggestionMatch) {
        numberedMatch = trimmedLine.match(/^[\*\s]*(\d+)\)\s*(.+)$/);
      }

      if (suggestionMatch) {
        const label = suggestionMatch[1];
        let text = suggestionMatch[2];

        // Remove markdown formatting from text (matching backend cleanup)
        text = text.replace(/\*\*/g, ''); // Remove bold markers
        text = text.replace(/^\*+/, ''); // Remove leading asterisks
        // Remove any remaining label prefix as fallback
        text = text.replace(/^[\*\s]*[A-Z]{1,2}\)\s*/, '');
        text = text.replace(/^[\*\s]*[A-Z]{1,2}\.\s*/, '');
        text = text.trim();

        // Store payload in Map; encode key as a CSS class so it survives
        // Angular's HTML sanitizer (which strips data-* and id attributes).
        const suggKey = `isg-${++this.suggestionCounter}`;
        this.inlineSuggestions.set(suggKey, { label, text });
        const clickableElement = `<div class="inline-suggestion ${suggKey}"><strong>${label})</strong> ${text}</div>`;
        processedLines.push(`||SUGGESTION||${clickableElement}||SUGGESTION||`);
      } else if (numberedMatch) {
        // Convert number to letter (1 -> A, 2 -> B, etc.) - matching backend logic
        const num = parseInt(numberedMatch[1]);
        if (num >= 1 && num <= 26) {
          const label = String.fromCharCode(64 + num); // 65 is 'A', so 64+1='A'
          let text = numberedMatch[2];

          // Remove markdown formatting from text (matching backend cleanup)
          text = text.replace(/\*\*/g, ''); // Remove bold markers
          text = text.replace(/^\*+/, ''); // Remove leading asterisks
          // Remove any remaining label prefix as fallback
          text = text.replace(/^[\*\s]*\d+\)\s*/, '');
          text = text.replace(/^[\*\s]*\d+\.\s*/, '');
          text = text.trim();

          const suggKey = `isg-${++this.suggestionCounter}`;
          this.inlineSuggestions.set(suggKey, { label, text });
          const clickableElement = `<div class="inline-suggestion ${suggKey}"><strong>${label})</strong> ${text}</div>`;
          processedLines.push(`||SUGGESTION||${clickableElement}||SUGGESTION||`);
        } else {
          processedLines.push(line);
        }
      } else {
        // Not a suggestion item, just add the line as-is
        processedLines.push(line);
      }
    }

    html = processedLines.join('\n');

    // Convert markdown code blocks to HTML
    html = html
      // Code blocks (```code```)
      .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
      // Remove ### symbols but keep text as regular size (not headings).
      // Match both at line-start (^\n context) AND after an existing <br> tag,
      // because the incoming content sometimes has "<br>###" pre-mixed in.
      .replace(/^###\s*/gm, '').replace(/<br>###\s*/g, '<br>')
      .replace(/^##\s*/gm, '').replace(/<br>##\s*/g, '<br>')
      .replace(/^#\s*/gm, '').replace(/<br>#\s*/g, '<br>')
      // Bold (**text**) - but not in inline-suggestion divs
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Italic (*text*)
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Line breaks
      .replace(/\n/g, '<br>');

    // Remove <br> tags around inline-suggestion divs and clean up extra breaks
    html = html.replace(/\|\|SUGGESTION\|\|/g, '')
      .replace(/<br>(<div class="inline-suggestion)/g, '$1')
      .replace(/(<\/div>)<br>/g, '$1')
      .replace(/(<br>\s*){3,}/g, '<br><br>'); // keep double-<br> (blank line), collapse 3+ to 2

    // Sanitize through Angular's allowlist (strips <script>, event handlers,
    // javascript: URIs, onerror / onload attributes, etc.) before marking safe.
    // This preserves formatting tags (<strong>, <em>, <br>, <pre>, <code>,
    // <div>, <span>) while blocking all XSS vectors.
    const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
    return this.sanitizer.bypassSecurityTrustHtml(sanitized);
  }

  openHistoryView(event: any) {
    /**
     * Emit an event to open a history/tool-data view.
     * - event: object expected to contain a `tool_data` payload.
     */
    this.onEmit.emit(event.tool_data);
  }

  updateLastChat() {
    /**
     * Persists the current chat state (thread id + messages) into the
     * AI streaming service's last-chat cache.
     */
    let obj: any = {
      threadId: this.threadId,
      messages: this.messages

    };
    this.aiStreamingService.setLastChat(obj);
  }

  ngOnDestroy(): void {
    /**
     * Angular lifecycle hook - OnDestroy
     * - Cleans up RxJS subscriptions
     * - Closes any open streams
     * - Removes persisted thread id and global handlers
     */
    this.destroy$.next();
    this.destroy$.complete();
    this.aiStreamingService.closeStream();
    localStorage.removeItem('t_id');
    // remove global keydown listener added in ngOnInit
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
  }


  /**
   * Copy a chat message's rendered HTML content to the clipboard.
   * @param content HTML string containing the message to copy
   */
  copyMsg(content: any) {
    if (typeof content !== 'string' && content.changingThisBreaksApplicationSecurity) {
      content = content.changingThisBreaksApplicationSecurity;
    }

    const text = this.stripHtml(content);
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.toaster.pop({ type: 'success', text: MessageEnum.CHAT_CONTENT_COPY_SUCCESS }, { backgroundColor: 'transparent' });
      })
      .catch((err) => {
        console.error(err);
      });
  }

  /**
   * Strip HTML tags from a string and return plain text.
   * Uses a DOM element to decode HTML entities and remove markup.
   * @param html String containing HTML
   * @returns Plain text with tags removed
   */
  stripHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  tryAgain() {
    const lastHumanChat = this.messages.findLast((item: any) => item.role === 'human');
    let content = lastHumanChat?.content;

    // Extract HTML string from SafeHtml object if needed
    if (typeof content !== 'string' && content?.changingThisBreaksApplicationSecurity) {
      content = content.changingThisBreaksApplicationSecurity;
    }

    // Strip HTML tags to get plain text
    this.sendInput = this.stripHtml(content || '');
    this.send();
  }

  removeAttachment(index: number) {
    this.attachments.splice(index, 1);
  }

  /**
   * Generic card submit handler - works for all card types
   */
  onCardSubmit(cardType: string | null, metadata: any) {
    if (!cardType) return;

    const config = this.cardConfigs[cardType];
    if (!config) {
      console.error('Card configuration not found for type:', cardType);
      return;
    }

    this.CreateNew(config, cardType, metadata);
  }

  /**
   * Create new application / page / UI object
   */
  CreateNew(config: any, cardType: string, metadata: any) {
    console.log("Creating new entity with config:", config, "and card type:", cardType);
    if (cardType === CardType.CREATE_WEB_TEST_SCRIPT || cardType === CardType.CREATE_MOBILE_TEST_SCRIPT) {
      this.openWebScriptModal(metadata, cardType, config);
    } else if (cardType === CardType.CREATE_RELEASE || cardType === CardType.CREATE_ENGINE) {
      this.openGenericModal(cardType, false, config, metadata);
    } else if (cardType === CardType.UPDATE_RELEASE || cardType === CardType.UPDATE_ENGINE) {
      this.openGenericModal(cardType, true, config, metadata);
    } else if (cardType === CardType.CREATE_TEST_PLAN || cardType === CardType.UPDATE_TEST_PLAN) {
      this.openCreateTestPlanModal(config, cardType, metadata);
    } else if (cardType === CardType.START_EXECUTION) {
      this.openStartExecutionModal();
    } else if (cardType === CardType.CREATE_DEVICE) {
      this.openCreateDeviceModal(config, cardType, metadata);
    } else if (cardType === CardType.UPADET_DEVICE) {
      this.openCreateDeviceModal(config, cardType, metadata, true);
    } else if (cardType === CardType.ADD_MISSING_TC) {
      this.openAddMissingTCModal(config, cardType, metadata);
    } else if (cardType === CardType.CREATE_API_TEST_SCRIPT) {
      this.openApiAutomation(config, metadata);
    } else if (cardType === CardType.CREATE_MAPPING) {
      this.openMappingModal(config);
    } else if (cardType === CardType.UPDATE_MAPPING_GIT_URL) {
      this.openMappingConfigsModal(config, metadata, true);
    } else if (cardType === CardType.UPDATE_MOST_IMPACTED_CARD) {
      this.openDefectReportUploadModal(metadata)
    } else {
      this.openAppCreateGenericModal(config, cardType, metadata);
    }
  }

  getStoryIds(versionId: string): Observable<string[]> {
    return this.agenticAiService.getStoryIds(this.projectId, versionId).pipe(
      map((res: any) => {
        const responseData = res.map((item: any) => {
          return { ...item, id: item.id || uuidv4() };
        });
        return [...new Set(responseData.map((item: any) => item.key))] as string[];
      })
    );
  }

  openAddMissingTCModal(config: any, cardType: string, metadata: any) {
    const configs = {
      title: "Add Missing Test Cases",
      data: {
        storyIds: [],
        isLoadingStoryIds: true
      }
    };
    const modalRef = this.modalService.open(AddMissingTestCaseModalComponent, {
      centered: true,
      size: 'md',
    });
    modalRef.componentInstance.configs = configs;

    // Fetch story IDs and update modal
    this.getStoryIds(metadata?.result?.[0]?.data?.prefilledData?.versionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (storyIds: string[]) => {
          modalRef.componentInstance.configs.data.storyIds = storyIds;
          modalRef.componentInstance.configs.data.isLoadingStoryIds = false;
        },
        error: (error: any) => {
          console.error('Error loading story IDs:', error);
          modalRef.componentInstance.configs.data.isLoadingStoryIds = false;
          this.toaster.pop({ type: 'error', text: MessageEnum.LOAD_USER_STORY_ID_FAILURE }, { backgroundColor: 'white' });
        }
      });

    modalRef.result.then((result: any) => {
      if (result?.value) {
        const d = result.value;
        this.sendInput = [
          `1. Story ID\n${d.storyId}`,
          `2. Story\n${d.userStory}`,
          `3. Test Case ID\n${d.testCaseId}`,
          `4. Test Case Description\n${d.testCaseDescription}`,
          `5. Prerequisites\n${d.prerequisites}`,
          `6. Test Steps\n${d.testSteps}`,
          `7. Expected Result\n${d.expectedResult}`
        ].join('\n\n');
        this.send();
      }
    }, () => { });
  }

  openCreateDeviceModal(config: any, cardType: string, metadata: any, isUpdate?: boolean) {
    const modalRef = this.modalService.open(CreateDeviceModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
    });
    modalRef.componentInstance.projectId = this.projectId;
    modalRef.componentInstance.isAgenticView = true;
    if (isUpdate) {
      modalRef.componentInstance.device = metadata.result[0]?.data?.prefilledData;
    }
    modalRef.result.then((result: any) => {
      if (result?.prompt) {
        this.sendInput = result.prompt;
        if (result?.serviceSecrets) {
          this.pendingSecrets = result.serviceSecrets;
        }
        this.send();
      }
    }, () => { });
  }

  /**
   * Get dynamic fields with populated options based on loaded data
   * @param preserveValues - Current form values to preserve when updating fields
   * @param metadata - Metadata containing prefilled data from AI response
   */
  getDynamicFields(cardType: string, preserveValues?: any, metadata?: any): any[] {
    let fields = this.getBaseFields(cardType);

    switch (cardType) {
      case CardType.CREATE_PAGE:
        this.populateApplicationField(fields, preserveValues);
        break;

      case CardType.CREATE_UI_OBJECT:
        this.configureUiObjectFields(fields, preserveValues, metadata);
        break;

      case CardType.CREATE_UI_LOCATOR:
      case CardType.CREATE_MOBILE_UI_LOCATOR:
        this.populateApplicationField(fields, preserveValues);
        this.populatePageField(fields, preserveValues);
        this.populateUiObjectField(fields, preserveValues);
        break;

      case CardType.CREATE_MOBILE_UI_OBJECT:
        this.populateApplicationField(fields, preserveValues);
        this.populatePageField(fields, preserveValues);
        break;
    }

    return fields;
  }

  private getBaseFields(cardType: string): any[] {
    const fieldMappings: Record<string, any> = {
      [CardType.CREATE_APPLICATION]: addApplicationFields,
      [CardType.CREATE_PAGE]: addPageFields,
      [CardType.CREATE_UI_OBJECT]: addUiObjectFields,
      [CardType.CREATE_UI_LOCATOR]: addLocatorFields,
      [CardType.CREATE_TEST_SCRIPT]: addTestScriptFields,
      [CardType.CREATE_TEST_SUITE]: addTestSuiteFields,
      [CardType.CREATE_REUSABLE_FOLDER]: addComponentFolderFields,
      [CardType.CREATE_REUSABLE_SCRIPT]: addComponentFields,
      [CardType.CREATE_PARAMETER]: addParameterFields,
      [CardType.CREATE_MOBILE_UI_OBJECT]: addUiObjectFieldsMobile,
      [CardType.CREATE_MOBILE_UI_LOCATOR]: addLocatorFieldsMobile
    };

    const baseFields = fieldMappings[cardType];
    return baseFields ? JSON.parse(JSON.stringify(baseFields)) : [];
  }

  private populateApplicationField(fields: any[], preserveValues?: any): void {
    const appField = fields.find(f => f.key === 'applicationName');
    if (appField && this.applications) {
      appField.options = this.applications;
      appField.isLoading = this.isApplicationsLoading;
      if (preserveValues?.applicationName) {
        appField.value = preserveValues.applicationName;
      }
    }
  }

  private populatePageField(fields: any[], preserveValues?: any): void {
    const pageField = fields.find(f => f.key === 'pageName');
    if (pageField) {
      pageField.options = this.pages;
      pageField.isLoading = this.isPagesLoading;
      if (preserveValues?.pageName) {
        pageField.value = preserveValues.pageName;
      }
    }
  }

  private populateUiObjectField(fields: any[], preserveValues?: any): void {
    const uiObjectField = fields.find(f => f.key === 'uiObjectName');
    if (uiObjectField) {
      uiObjectField.options = this.uiObjects;
      uiObjectField.isLoading = this.isUiObjectsLoading;
      if (preserveValues?.uiObjectName) {
        uiObjectField.value = preserveValues.uiObjectName;
      }
    }
  }

  private configureUiObjectFields(fields: any[], preserveValues?: any, metadata?: any): void {
    const selectedAppType = this.determineApplicationType(preserveValues, metadata);
    this.addBrowserOrPlatformField(fields, selectedAppType);
    this.populateUiObjectApplicationField(fields, preserveValues, metadata);
    this.populateUiObjectPageField(fields, preserveValues, metadata);
  }

  private determineApplicationType(preserveValues?: any, metadata?: any): string | undefined {
    if (preserveValues?.applicationName && this.applications) {
      const selectedApp = this.applications.find((app: any) => app.value === preserveValues.applicationName);
      if (selectedApp) {
        return selectedApp.paradigm || selectedApp.applicationType;
      }
    }
    return metadata?.result?.[0]?.data?.prefilledData?.application_type;
  }

  private addBrowserOrPlatformField(fields: any[], selectedAppType?: string): void {
    if (!selectedAppType) return;

    const normalizedType = selectedAppType.toUpperCase();
    const showBrowser = this.shouldShowBrowserField(normalizedType);
    const showPlatform = this.shouldShowPlatformField(normalizedType);

    fields = fields.filter((f: any) => f.key !== 'browser' && f.key !== 'platform');

    const locatorIndex = fields.findIndex((f: any) => f.key === 'locator');
    if (locatorIndex === -1) return;

    if (showPlatform) {
      fields.splice(locatorIndex + 1, 0, this.createPlatformField());
    } else if (showBrowser) {
      fields.splice(locatorIndex + 1, 0, this.createBrowserField());
    }
  }

  private shouldShowBrowserField(normalizedType: string): boolean {
    return normalizedType === 'WEB' ||
      normalizedType === 'MOBILE-MOBILE_WEB' ||
      normalizedType === 'MOBILE-MOBILE-WEB';
  }

  private shouldShowPlatformField(normalizedType: string): boolean {
    return normalizedType.startsWith('MOBILE-') || normalizedType === 'MOBILE';
  }

  private createPlatformField(): any {
    return {
      key: "platform",
      name: "Platform",
      type: "select-card",
      validations: { required: true },
      options: [
        {
          icon: 'images/virtusa-icons/android.svg',
          label: "Android",
          value: "ANDROID",
        },
        {
          icon: 'images/virtusa-icons/ios.svg',
          label: "iOS",
          value: "IOS",
        },
      ],
      customClass: "mt-3",
      wrapperClass: "col-md-12"
    };
  }

  private createBrowserField(): any {
    return {
      key: "browser",
      name: "Browser",
      type: "select-card",
      validations: { required: true },
      options: [
        {
          icon: 'images/virtusa-icons/chrome.svg',
          label: "Google Chrome",
          value: "Chrome",
        },
        {
          icon: 'images/virtusa-icons/firefox.svg',
          label: "Mozilla Firefox",
          value: "Firefox",
        },
        {
          icon: 'images/virtusa-icons/edge.svg',
          label: "Microsoft Edge",
          value: "Edge",
        },
      ],
      customClass: "mt-3",
      wrapperClass: "col-md-12"
    };
  }

  private populateUiObjectApplicationField(fields: any[], preserveValues?: any, metadata?: any): void {
    const uiAppField = fields.find(f => f.key === 'applicationName');
    if (!uiAppField || !this.applications) return;

    uiAppField.options = this.applications;
    uiAppField.isLoading = this.isApplicationsLoading;

    const metadataAppName = metadata?.result?.[0]?.data?.prefilledData?.application_name;
    if (metadataAppName) {
      const matchedApp = this.applications.find((app: any) => app.name === metadataAppName);
      if (matchedApp) {
        uiAppField.value = matchedApp.value;
      }
    } else if (preserveValues?.applicationName) {
      uiAppField.value = preserveValues.applicationName;
    }
  }

  private populateUiObjectPageField(fields: any[], preserveValues?: any, metadata?: any): void {
    const uiPageField = fields.find(f => f.key === 'pageName');
    if (!uiPageField) return;

    uiPageField.options = this.pages;
    uiPageField.isLoading = this.isPagesLoading;

    const metadataPageName = metadata?.result?.[0]?.data?.prefilledData?.page_name;
    if (metadataPageName) {
      const matchedPage = this.pages.find((page: any) => page.name === metadataPageName);
      if (matchedPage) {
        uiPageField.value = matchedPage.value;
      }
    } else if (preserveValues?.pageName) {
      uiPageField.value = preserveValues.pageName;
    }
  }

  openAppCreateGenericModal(config: any, cardType: string, metadata?: any) {
    // Open modal immediately
    let modalRef = this.modalService.open(ApplicationCreateModalComponent, {
      centered: true,
      size: "lg",
    });

    // Store modal reference for updating
    this.currentModalRef = modalRef;

    // Set loading state
    this.isApplicationsLoading = true;

    // Get dynamic fields with loading state
    const dynamicFields = this.getDynamicFields(cardType, undefined, metadata);

    // Load applications
    if (this.projectId && this.automationProjectId) {
      this.agenticAiService.loadApplications(this.projectId, this.automationProjectId).subscribe({
        next: (data) => {
          this.applications = data;
          this.isApplicationsLoading = false;
          // Update fields with loaded applications
          const updatedFields = this.getDynamicFields(cardType, undefined, metadata);
          modalRef.componentInstance.fields = updatedFields;

          // For CREATE_UI_OBJECT, patch form value and load pages if metadata has application name
          if (cardType === CardType.CREATE_UI_OBJECT) {
            const appField = updatedFields.find(f => f.key === 'applicationName');
            if (appField?.value && modalRef.componentInstance.dynamicForm) {
              setTimeout(() => {
                modalRef.componentInstance.dynamicForm.patchValue({
                  applicationName: appField.value
                });
              }, 0);

              // Load pages for the pre-selected application
              this.isPagesLoading = true;
              // Update fields to show loading state
              const loadingFields = this.getDynamicFields(cardType, { applicationName: appField.value }, metadata);
              modalRef.componentInstance.fields = loadingFields;

              this.onApplicationSelect(appField.value, cardType, () => {
                // Pages loaded, now set loading to false
                this.isPagesLoading = false;
                // Update fields with loaded pages and loading state false
                const fieldsWithPages = this.getDynamicFields(cardType, { applicationName: appField.value }, metadata);
                modalRef.componentInstance.fields = fieldsWithPages;

                // Check if page_name is also in metadata
                const metadataPageName = metadata?.result?.[0]?.data?.prefilledData?.page_name;
                if (metadataPageName) {
                  const pageField = fieldsWithPages.find(f => f.key === 'pageName');
                  if (pageField?.value && modalRef.componentInstance.dynamicForm) {
                    setTimeout(() => {
                      modalRef.componentInstance.dynamicForm.patchValue({
                        pageName: pageField.value
                      });
                    }, 0);
                  }
                }
              });
            }
          }
        },
        error: (error) => {
          console.error('Error loading applications:', error);
          this.isApplicationsLoading = false;
          // Update fields even on error
          const updatedFields = this.getDynamicFields(cardType, undefined, metadata);
          modalRef.componentInstance.fields = updatedFields;
        }
      });
    }

    // Set up field change handler for cascading dropdowns
    modalRef.componentInstance.onFieldChange = (field: any, value: any) => {
      if (field.key === 'applicationType') {
        // Handle application type changes to add browser/platform fields dynamically
        const currentValues = modalRef.componentInstance.dynamicForm.value;
        currentValues.applicationType = value;

        let updatedFields = this.getDynamicFields(cardType, currentValues, metadata);

        // Remove existing browser and platform fields if any
        updatedFields = updatedFields.filter((f: any) => f.key !== 'browser' && f.key !== 'platform');

        // Add platform and file upload for all MOBILE-* types
        if (value && value.startsWith('MOBILE-')) {
          updatedFields.push(
            {
              key: "platform",
              name: "Platform",
              type: "select-card",
              validations: {
                required: true,
              },
              options: [
                {
                  icon: 'images/virtusa-icons/android.svg',
                  label: "Android",
                  value: "ANDROID",
                },
                {
                  icon: 'images/virtusa-icons/ios.svg',
                  label: "iOS",
                  value: "IOS",
                },
              ],
              customClass: "mt-3",
            },
            {
              name: "Upload APK",
              id: "mobileFileName",
              key: "mobileFileName",
              type: "file-upload",
              placeholder: "APK",
              wrapperClass: "col-md-12",
              validations: {
                required: true,
              },
              configs: {
                fileTypes: ".apk, .ipa, .pdf",
                buttonLabel: "Choose file",
                placeholderText: "APK and IPA files only",
              },
            }
          );
        }

        // Add browser field for WEB and MOBILE-MOBILE_WEB
        if (value === 'Web' || value === 'MOBILE-Mobile-Web') {
          updatedFields.push({
            key: "browser",
            name: "Browser",
            type: "select-card",
            validations: {
              required: true,
            },
            options: [
              {
                icon: 'images/virtusa-icons/chrome.svg',
                label: "Google Chrome",
                value: "Chrome",
              },
              {
                icon: 'images/virtusa-icons/firefox.svg',
                label: "Mozilla Firefox",
                value: "Firefox",
              },
              {
                icon: 'images/virtusa-icons/edge.svg',
                label: "Microsoft Edge",
                value: "Edge",
              },
            ],
            customClass: "mt-3",
          });
        }


        modalRef.componentInstance.fields = updatedFields;
      } else if (field.key === 'applicationName') {
        // Set loading state
        this.isPagesLoading = true;
        const currentValues = modalRef.componentInstance.dynamicForm.value;
        currentValues.applicationName = value;

        // For CREATE_UI_OBJECT, dynamically add browser/platform based on selected application type
        if (cardType === CardType.CREATE_UI_OBJECT && this.applications) {
          const selectedApp = this.applications.find((app: any) => app.value === value);

          // Update fields with the selected application type to show correct browser/platform
          const fieldsWithType = this.getDynamicFields(cardType, currentValues, metadata);
          modalRef.componentInstance.fields = fieldsWithType;
        } else {
          // Update fields with loading state
          const loadingFields = this.getDynamicFields(cardType, currentValues, metadata);
          modalRef.componentInstance.fields = loadingFields;
        }

        // Load pages when application is selected
        this.onApplicationSelect(value, cardType, () => {
          // After loading, update fields with data
          this.isPagesLoading = false;
          const updatedFields = this.getDynamicFields(cardType, currentValues, metadata);
          modalRef.componentInstance.fields = updatedFields;
        });
      } else if (field.key === 'pageName') {
        // Set loading state
        this.isUiObjectsLoading = true;
        const currentValues = modalRef.componentInstance.dynamicForm.value;
        currentValues.pageName = value;

        // Update fields with loading state
        const loadingFields = this.getDynamicFields(cardType, currentValues, metadata);
        modalRef.componentInstance.fields = loadingFields;

        // Load UI objects when page is selected
        this.onPageSelect(value, cardType, () => {
          // After loading, update fields with data
          this.isUiObjectsLoading = false;
          const updatedFields = this.getDynamicFields(cardType, currentValues, metadata);
          modalRef.componentInstance.fields = updatedFields;
        });
      }
    };

    // Use switch case to configure modal based on cardType
    switch (cardType) {
      case CardType.CREATE_APPLICATION:
        modalRef.componentInstance.configs = {
          title: "Create a new application",
          saveBtnText: "Create",
        };
        modalRef.componentInstance.fields = dynamicFields;
        break;

      case CardType.CREATE_PAGE:
        modalRef.componentInstance.configs = {
          title: "Create a new page",
          saveBtnText: "Create",
        };
        modalRef.componentInstance.fields = dynamicFields;
        break;

      case CardType.CREATE_UI_OBJECT:
        modalRef.componentInstance.configs = {
          title: "Create a new UI object",
          saveBtnText: "Create",
        };
        modalRef.componentInstance.fields = dynamicFields;
        break;

      case CardType.CREATE_UI_LOCATOR:
        modalRef.componentInstance.configs = {
          title: "Create a new Locator",
          saveBtnText: "Create",
        };
        modalRef.componentInstance.fields = dynamicFields;
        break;

      case CardType.CREATE_TEST_SCRIPT:
        modalRef.componentInstance.configs = {
          title: "Create a new test script",
          saveBtnText: "Create",
        };
        modalRef.componentInstance.fields = dynamicFields;
        break;

      case CardType.CREATE_TEST_SUITE:
        modalRef.componentInstance.configs = {
          title: "Create a new test suite",
          saveBtnText: "Create",
        };
        modalRef.componentInstance.fields = dynamicFields;
        break;

      case CardType.CREATE_REUSABLE_FOLDER:
        modalRef.componentInstance.configs = {
          title: "Create a new component folder",
          saveBtnText: "Create",
        };
        modalRef.componentInstance.fields = dynamicFields;
        break;

      case CardType.CREATE_REUSABLE_SCRIPT:
        modalRef.componentInstance.configs = {
          title: "Create a new component",
          saveBtnText: "Create",
        };
        modalRef.componentInstance.fields = dynamicFields;
        break;

      case CardType.CREATE_PARAMETER:
        modalRef.componentInstance.configs = {
          title: "Add New Parameter",
          saveBtnText: "Create",
        };
        modalRef.componentInstance.fields = dynamicFields;
        break;
    }

    modalRef.result.then((result: any) => {
      if (result) {
        console.log(result);
        this.cardFormData = result?.value;

        // Build query using the card's custom query builder
        // Convert IDs to names before building query
        const dataWithNames = this.convertIdsToNames(this.cardFormData);
        const query = config.buildQuery(dataWithNames);

        this.sendInput = query;
        this.send();

        // Reset form
        this.resetCardForm(cardType);
      }
    });
  }
  /**
   * Reset card form to default values
   */
  private resetCardForm(cardType: string) {
    const config = this.cardConfigs[cardType];
    if (!config) return;

    this.cardFormData = {};
  }

  /**
   * Convert IDs to names for display in queries
   */
  private convertIdsToNames(data: any): any {
    const result = { ...data };

    // Convert application ID to name
    if (result.applicationName) {
      const app = this.applications.find(a => a.value === result.applicationName);
      if (app) result.applicationName = app.label;
    }

    // Convert page ID to name
    if (result.pageName) {
      const page = this.pages.find(p => p.value === result.pageName);
      if (page) result.pageName = page.label;
    }

    // Convert UI object ID to name
    if (result.uiObjectName) {
      const uiObj = this.uiObjects.find(u => u.value === result.uiObjectName);
      if (uiObj) result.uiObjectName = uiObj.label;
    }

    return result;
  }

  /**
   * Initialize card form when a card is detected
   */
  private initializeCardForm(cardType: string) {
    if (!this.cardConfigs[cardType]) return;
  }

  /**
   * Check if message contains any card marker
   */
  hasCardMarker(msg: any): boolean {
    if (!msg?.content) return false;
    const content = typeof msg.content === 'string' ? msg.content : msg.content.changingThisBreaksApplicationSecurity;
    return Object.values(CardType).some(cardType => content?.includes(`||${cardType}||`));
  }

  /**
   * Get card type from message content
   */
  getCardType(msg: any): string | null {
    if (!msg?.content) return null;
    const content = typeof msg.content === 'string' ? msg.content : msg.content.changingThisBreaksApplicationSecurity;

    for (const cardType of Object.values(CardType)) {
      if (content?.includes(`||${cardType}||`)) {
        // Initialize form when card is detected
        this.initializeCardForm(cardType);
        return cardType;
      }
    }
    return null;
  }

  /**
   * Get card configuration by type
   */
  getCardConfig(cardType: string | null): CardConfig | null {
    if (!cardType) return null;
    return this.cardConfigs[cardType] || null;
  }

  /**
   * Parse message content and split by card marker
   */
  parseCardContent(msg: any): { beforeCard: SafeHtml, hasCard: boolean, cardType: string | null, afterCard: SafeHtml } {
    if (!msg?.content) {
      return { beforeCard: '', hasCard: false, cardType: null, afterCard: '' };
    }

    const content = typeof msg.content === 'string' ? msg.content : msg.content.changingThisBreaksApplicationSecurity;

    // Find which card type is present
    let foundCardType: string | null = null;
    let cardMarker = '';

    for (const cardType of Object.values(CardType)) {
      const marker = `||${cardType}||`;
      if (content?.includes(marker)) {
        foundCardType = cardType;
        cardMarker = marker;
        break;
      }
    }

    if (!foundCardType) {
      return { beforeCard: msg.content, hasCard: false, cardType: null, afterCard: '' };
    }

    // Split content by card marker
    const parts = content.split(cardMarker);
    const beforeCardText = parts[0] || '';
    const afterCardText = parts[1] || '';

    return {
      beforeCard: this.sanitizer.bypassSecurityTrustHtml(
        this.sanitizer.sanitize(SecurityContext.HTML, beforeCardText) ?? ''
      ),
      hasCard: true,
      cardType: foundCardType,
      afterCard: this.sanitizer.bypassSecurityTrustHtml(
        this.sanitizer.sanitize(SecurityContext.HTML, afterCardText) ?? ''
      )
    };
  }

  openWebScriptModal(metadata: any, cardType: any, config: any): void {
    //pass test script data
    console.log("Metadata for web script modal:", metadata);
    const metadataResult = metadata?.result[0]?.data?.prefilledData || {};
    let data = {
      title: cardType === CardType.CREATE_MOBILE_TEST_SCRIPT ? "Generate a mobile test script" : "Generate a web test script",
      saveBtnText: "Generate",
      data: {
        scriptName: metadataResult?.script_name,
        testSuite: metadataResult?.test_suite_name,
        mappedTestCase: metadataResult?.test_case_name,
        showMobileDeviceWarning: false
      },
      fields: cardType === CardType.CREATE_MOBILE_TEST_SCRIPT ? mobileScriptFields : webScriptFields,
      tableData: {
        columns: testCaseVersionMoreDetails,
        rows: []
      }
    };
    let modalRef = this.modalService.open(WebScriptModalComponent, {
      centered: true,
      size: "xl",
      windowClass: "scrip-mapping-modal",
      backdrop: "static",
    });

    // Set initial fields with loading state
    const initialFields = cardType === CardType.CREATE_MOBILE_TEST_SCRIPT ? [...mobileScriptFields] : [...webScriptFields];
    const appField: any = initialFields.find(f => f.key === 'appName');
    if (appField) {
      // Don't set value yet since options are empty - wait for applications to load
      appField.isLoading = true;
    }

    // Set loading state for device field in mobile script
    if (cardType === CardType.CREATE_MOBILE_TEST_SCRIPT) {
      const deviceField: any = initialFields.find(f => f.key === 'device');
      if (deviceField) {
        deviceField.isLoading = true;
      }
    }

    data.fields = initialFields;

    modalRef.componentInstance.configs = data;

    // Load applications for the modal
    if (this.projectId && this.automationProjectId) {
      this.isApplicationsLoading = true;
      this.agenticAiService.loadApplications(this.projectId, this.automationProjectId).subscribe({
        next: (applications) => {
          this.applications = applications;
          this.isApplicationsLoading = false;

          // Update the application field in the modal's fields
          const updatedFields = cardType === CardType.CREATE_MOBILE_TEST_SCRIPT ? [...mobileScriptFields] : [...webScriptFields];
          const appFieldUpdate: any = updatedFields.find(f => f.key === 'appName');
          if (appFieldUpdate) {
            appFieldUpdate.options = applications;
            appFieldUpdate.value = (applications.find((item: any) => item.name === metadataResult?.application_name))?.value || '';
            appFieldUpdate.isLoading = false;
          }

          // Update modal configs with loaded applications
          modalRef.componentInstance.configs.fields = updatedFields;

          // Patch the form control value directly to ensure it's set
          if (appFieldUpdate?.value && modalRef.componentInstance.dynamicForm) {
            setTimeout(() => {
              modalRef.componentInstance.dynamicForm.patchValue({
                appName: appFieldUpdate.value
              });
            }, 0);
          }
        },
        error: (error) => {
          console.error('Error loading applications:', error);
          this.isApplicationsLoading = false;

          // Update loading state on error
          const updatedFields = cardType === CardType.CREATE_MOBILE_TEST_SCRIPT ? [...mobileScriptFields] : [...webScriptFields];
          const appFieldError: any = updatedFields.find(f => f.key === 'appName');
          if (appFieldError) {
            appFieldError.isLoading = false;
          }
          modalRef.componentInstance.configs.fields = updatedFields;
        }
      });

      // Load devices for mobile applications
      if (cardType === CardType.CREATE_MOBILE_TEST_SCRIPT) {
        this.isDevicesLoading = true;
        this.agenticAiService.loadDevices(this.projectId).subscribe({
          next: (devices) => {
            this.devices = devices;
            this.isDevicesLoading = false;

            // Update the device field in the modal's fields
            const updatedFields = [...mobileScriptFields];
            const deviceFieldUpdate: any = updatedFields.find(f => f.key === 'device');
            if (deviceFieldUpdate) {
              deviceFieldUpdate.options = devices;
              deviceFieldUpdate.isLoading = false;
              deviceFieldUpdate.isDisabled = devices.length === 0;
            }

            // Update application field as well
            const appFieldUpdate: any = updatedFields.find(f => f.key === 'appName');
            if (appFieldUpdate) {
              appFieldUpdate.options = this.applications;
              appFieldUpdate.value = (this.applications.find((item: any) => item.name === metadataResult?.application_name))?.value || '';
              appFieldUpdate.isLoading = false;
            }

            // Set warning flag if no devices available
            modalRef.componentInstance.configs.data.showMobileDeviceWarning = devices.length === 0;

            // Update modal configs with loaded devices
            modalRef.componentInstance.configs.fields = updatedFields;

          },
          error: (error) => {
            console.error('Error loading devices:', error);
            this.isDevicesLoading = false;

            // Update loading state on error
            const updatedFields = [...mobileScriptFields];
            const deviceFieldError: any = updatedFields.find(f => f.key === 'device');
            if (deviceFieldError) {
              deviceFieldError.isLoading = false;
              deviceFieldError.options = [];
              deviceFieldError.isDisabled = true;
            }

            // Set warning flag when no devices available
            modalRef.componentInstance.configs.data.showMobileDeviceWarning = true;

            modalRef.componentInstance.configs.fields = updatedFields;
          }
        });
      }
    }

    modalRef.result.then((result: any) => {
      if (result) {
        const data = result?.value || result;
        // Build credentials block
        const credentialLines = (data.credentials || [])
          .map((cred: any) =>
            `- username : ${cred.username || ''}\n  password : ${cred.password || ''}\n  role : ${cred.role || ''}\n  remarks: ${cred.remarks || ''}`
          )
          .join('\n');

        let prompt = "";

        if (cardType === CardType.CREATE_MOBILE_TEST_SCRIPT) {
          prompt = credentialLines ? config?.buildQuery(data) + `\n\nCredentials:\n${credentialLines}` : config?.buildQuery(data);
        } else {
          prompt = credentialLines
            ? `Generate automation script steps for:\nURL: ${data.url || ''}\n\nCredentials:\n${credentialLines}`
            : `Generate automation script steps for:\nURL: ${data.url || ''}`;
        }

        this.sendInput = prompt;
        this.send();
      }
    });
  }

  /**
   * Handle application selection - loads pages for selected application
   */
  onApplicationSelect(applicationId: string, cardType: string, callback?: () => void): void {
    this.selectedApplicationId = applicationId;

    if (applicationId && this.projectId && this.automationProjectId) {
      this.agenticAiService.loadPages(this.projectId, this.automationProjectId, applicationId).subscribe({
        next: (data) => {
          this.allPages = data;
          this.pages = [...data];
          if (callback) callback();
        },
        error: (error) => {
          console.error('Error loading pages:', error);
          this.isPagesLoading = false;
          if (callback) callback();
        }
      });
    } else {
      this.pages = [];
      this.allPages = [];
      if (callback) callback();
    }

    // Reset dependent selections
    if (this.cardFormData[cardType]) {
      this.cardFormData[cardType].page = '';
      this.cardFormData[cardType].uiObject = '';
    }
    this.selectedPageId = null;
    this.uiObjects = [];
  }

  /**
   * Handle page selection - loads UI objects for selected page
   */
  onPageSelect(pageId: string, cardType: string, callback?: () => void): void {
    this.selectedPageId = pageId;

    if (pageId && this.projectId && this.automationProjectId && this.selectedApplicationId) {
      this.agenticAiService.loadUiObjects(this.projectId, this.automationProjectId, this.selectedApplicationId, pageId).subscribe({
        next: (data) => {
          this.uiObjects = data;
          if (callback) callback();
        },
        error: (error) => {
          console.error('Error loading UI objects:', error);
          this.isUiObjectsLoading = false;
          if (callback) callback();
        }
      });
    } else {
      this.uiObjects = [];
      if (callback) callback();
    }

    // Reset dependent selection
    if (this.cardFormData[cardType]) {
      this.cardFormData[cardType].uiObject = '';
    }
  }

  /**
   * Opens the editor view in a new tab with isAgentic parameter set to true
   */
  openEditorView(isAgentic: boolean = true): void {
    if (!this.projectId || !this.automationProjectId || !this.scriptId) {
      console.error("Missing required IDs to open editor view");
      return;
    }

    const newUrl = this.configService.buildAppUrl(
      `project/${this.projectId}/automation/${this.automationProjectId}/script/${this.scriptId}/editor-view?isAgentic=${isAgentic}`
    );
    window.open(newUrl, "_blank");
  }


  //execution modals
  openGenericModal(type: string, isEdit: boolean = false, config?: any, metadata?: any) {
    const modalRef = this.modalService.open(AgentCreateModalComponent, {
      centered: true,
      size: "lg",
      windowClass: "add-item-modal",
      backdrop: "static",
    });

    const prefilledData = this.extractPrefilledData(metadata);
    this.configureGenericModal(modalRef, type, isEdit, prefilledData);
    this.handleGenericModalResult(modalRef, type, config, prefilledData);
  }

  private extractPrefilledData(metadata?: any): any {
    return metadata?.result?.[0]?.data?.prefilledData || {};
  }

  private configureGenericModal(modalRef: any, type: string, isEdit: boolean, prefilledData: any): void {
    const modalConfig = this.getGenericModalConfig(type, isEdit);

    if (!modalConfig) {
      console.error("Invalid type for modal:", type);
      return;
    }

    modalRef.componentInstance.configs = {
      title: modalConfig.displayName,
      saveBtnText: modalConfig.saveBtnText,
      isEdit: isEdit,
      model: prefilledData,
    };
    modalRef.componentInstance.fields = modalConfig.fields;
  }

  private getGenericModalConfig(type: string, isEdit: boolean): { displayName: string; saveBtnText: string; fields: any[] } | null {
    if (this.isEngineType(type)) {
      return this.getEngineModalConfig(isEdit);
    }

    if (this.isReleaseType(type)) {
      return this.getReleaseModalConfig(isEdit);
    }

    return null;
  }

  private isEngineType(type: string): boolean {
    return type === CardType.CREATE_ENGINE || type === CardType.UPDATE_ENGINE;
  }

  private isReleaseType(type: string): boolean {
    return type === CardType.CREATE_RELEASE || type === CardType.UPDATE_RELEASE;
  }

  private getEngineModalConfig(isEdit: boolean): { displayName: string; saveBtnText: string; fields: any[] } {
    return {
      displayName: isEdit ? "Update Execution Engine" : "Add New Execution Engine",
      saveBtnText: isEdit ? "Update" : "Add",
      fields: isEdit ? agentFieldsEdit : agentFields
    };
  }

  private getReleaseModalConfig(isEdit: boolean): { displayName: string; saveBtnText: string; fields: any[] } {
    return {
      displayName: isEdit ? "Update Release" : "Create a new release",
      saveBtnText: isEdit ? "Update" : "Add",
      fields: isEdit ? releaseFieldsEdit : releaseFields
    };
  }

  private handleGenericModalResult(modalRef: any, type: string, config: any, prefilledData: any): void {
    modalRef.result.then((result: any) => {
      if (result) {
        this.processGenericModalResult(result, type, config, prefilledData);
      }
    });
  }

  private processGenericModalResult(result: any, type: string, config: any, prefilledData: any): void {
    this.cardFormData = result?.value;
    this.restoreDisabledNameField(type, prefilledData);

    const dataWithNames = this.convertIdsToNames(this.cardFormData);
    const query = config.buildQuery(dataWithNames);

    this.sendInput = query;
    this.send();
  }

  private restoreDisabledNameField(type: string, prefilledData: any): void {
    const isUpdateWithDisabledName = type === CardType.UPDATE_RELEASE || type === CardType.UPDATE_ENGINE;

    if (isUpdateWithDisabledName && !this.cardFormData?.['name'] && prefilledData?.['name']) {
      this.cardFormData = { ...this.cardFormData, name: prefilledData['name'] };
    }
  }

  openCreateTestPlanModal(config: any, type: any, metadata?: any): void {
    let planId = "";
    if (type === CardType.UPDATE_TEST_PLAN) {
      planId = metadata?.result?.[0]?.data?.prefilledData?.id || '';
    }
    const modalRef = this.modalService.open(TestPlanModalComponent, {
      centered: true,
      size: "lg",
      backdrop: "static",
    });

    modalRef.componentInstance.configs = {
      title: planId ? "Update Test Plan" : "Create a test plan",
      saveBtnText: planId ? "Update" : "Create",
      sortOptions: [
        { id: "test-case-count", label: "Test Case Count", direction: "asc" },
        { id: "name", label: "Name", direction: "asc" },
      ],
      paginationInfoTSG: {},
      // accordionData: [],
      projectId: this.projectId,
      automationProjectId: this.automationProjectId,
    };
    modalRef.componentInstance.planId = planId;

    modalRef.result
      .then((result: any) => {
        if (result) {
          this.sendInput = 'View test plans for execution';
          this.send();
        }
      })
      .catch(() => { });
  }

  openStartExecutionModal(): void {
    let modalRef = this.modalService.open(StartExecutionModalComponent, {
      centered: true,
      size: "lg",
      backdrop: "static",
    });

    modalRef.componentInstance.configs = {
      title: "Start Execution",
      saveBtnText: "Start",
    };
    modalRef.componentInstance.projectId = this.projectId;
    modalRef.componentInstance.threadId = this.threadId;

    modalRef.result.then((result: any) => {
      if (result) {
        console.log(result);
        this.sendInput = 'Start execution';
        this.send();
      }
    });
  }

  openApiAutomation(config: any, metadata?: any) {
    this.validateRecordedActivities(config, metadata);
  }

  validateRecordedActivities(config?: any, metadata?: any) {
    const modalRef = this.modalService.open(
      ValidateRecordedActivitiesComponent,
      {
        centered: true,
        size: "lg",
        windowClass: "add-item-modal",
        backdrop: 'static',
      }
    );
    modalRef.componentInstance.type = "api";
    modalRef.componentInstance.isAgentic = true;
    modalRef.componentInstance.configs = {
      title: "Generate API test script"
    };
    modalRef.componentInstance.model = {
      testCase: metadata.result[0]?.data?.prefilledData?.test_case_name || '',
      testCaseId: metadata.result[0]?.data?.prefilledData?.test_case_id || '09d2aa07-1898-45ee-84b8-d21b09482d72',
      testSuiteName: metadata.result[0]?.data?.prefilledData?.test_suite_name || '',
      scriptName: metadata.result[0]?.data?.prefilledData?.script_name || '',
    };
    modalRef.componentInstance.routeParams = {
      projectId: this.projectId,
      automationId: this.automationProjectId,
      scriptId: metadata.result[0]?.data?.prefilledData?.script_id || this.scriptId || 'c521d14c-d485-4b95-b9e9-89390a1b9808'
    };

    modalRef.result.then((result) => {
      if (result) {
        if (config?.buildQuery) {
          let query = `Generate automation API script steps for:\nMappedTestCase: ${result?.mappedTestCase}`;
          if (result?.file) {
            this.attachments.push(result.file)
          }
          this.sendInput = query;
          this.send();
        }
      }
    }).catch(() => { });
  }


  downloadExcel(event: any) {
    const data = event?.tool_data?.result[0]?.data;
    const combinedTables = data?.combined_tables || data?.tableData;
    const fileName = data?.fileName || combinedTables?.[0]?.testCaseId || combinedTables?.testCaseId || combinedTables?.tableName || combinedTables?.[0]?.tableName || 'download.xlsx';

    const toolDataId: string = event?.tool_data?.id ?? '';
    if(toolDataId.startsWith("impacted_testcases_") || toolDataId.startsWith("most_impacted_testcases_") || toolDataId === "recommendations_table"){
      this.exportToexcel(data, toolDataId);
      return;
    }
    
    if (!combinedTables || combinedTables.length === 0) {
      if (event?.tool_data?.result[0]?.id === "test_suite_table") {
        this.downloadTestSuite(data, event);
      } else {
        this.toaster.pop({ type: 'error', text: MessageEnum.NO_DATA_AVAILABLE_TO_DOWNLOAD }, { backgroundColor: 'white' });
        return;
      }
    }

    let downloadTable;

    if (combinedTables?.length > 0) {
      downloadTable = combinedTables.map((table: any) => {
        // Convert rawData from object format to array of arrays if needed
        let rawDataArray = Array.isArray(table.rawData)
          ? table.rawData
          : Object.values(table.rawData || {});

        // Transform each row into a comma-separated string with quoted values
        const formattedRawData = rawDataArray.map((row: any) => {
          const values = Array.isArray(row) ? row : Object.values(row);
          return values.map((val: any) => `'${val}'`).join(', ');
        });

        return {
          ...table,
          rawData: formattedRawData,
          fileName: fileName
        };
      });
    } else {
      let rawDataArray = Array.isArray(combinedTables.rawData)
        ? combinedTables.rawData
        : Object.values(combinedTables.rawData || {});

      // Transform each row into a comma-separated string with quoted values
      const formattedRawData = rawDataArray.map((row: any) => {
        const values = Array.isArray(row) ? row : Object.values(row);
        return values.map((val: any) => `'${val}'`).join(', ');
      });

      downloadTable = [{
        columns: combinedTables?.columns,
        rawData: formattedRawData,
        fileName: fileName
      }];
    }

    this.downloadTDGExcelFile(downloadTable, fileName)
  }

  downloadTDGExcelFile(downloadTable: any, fileName: any) {
    this.isDownloadActive = true;
    this.agenticAiService.downloadExcelTDG(downloadTable).subscribe({
      next: (blob: Blob) => {
        this.isDownloadActive = false;
        this.tdgService.triggerDownload(blob, fileName);
      },
      error: (error) => {
        this.isDownloadActive = false;
        this.toaster.pop({ type: 'error', text: MessageEnum.EXCEL_DOWNLOAD_FAILURE }, { backgroundColor: 'white' });
      }
    });
  }

  downloadTCGExcelFile(payload: any) {
    this.agenticAiService.downloadExcelTCG(this.projectId, payload).subscribe({
      next: (response: any) => {
        window.open(response?.data, "_blank");
      },
      error: (error: any) => {
        let errMessage = error?.message ? error?.message : MessageEnum.EXCEL_DOWNLOAD_FAILURE;
        this.toaster.pop({ type: 'error', text: errMessage });
      }
    });
  }

  downloadTestSuite(data: any, event: any) {
    const fileName = event?.tool_data?.test_suite || 'test_suite'
    const formattedData = data?.rows?.map((row: any) => ({
      _id: uuidv4(),
      key: row.userStoryId,
      storydesc: row.storysummary,
      testCaseId: row.testCaseId,
      testCase: row.testCase,
      prerequisites: Array.isArray(row.prerequisites) ? row.prerequisites.filter((val: any) => val != null).map((val: string, idx: number) => `${idx + 1}. ${val}`).join(', ') : row.prerequisites || '',
      testScenario: Array.isArray(row.testSteps) ? row.testSteps.filter((val: any) => val != null).map((val: string, idx: number) => `${idx + 1}. ${val}`).join(', ') : row.testSteps || '',
      expectedResult: Array.isArray(row.expectedResults) ? row.expectedResults.filter((val: any) => val != null).map((val: string, idx: number) => `${idx + 1}. ${val}`).join(', ') : row.expectedResults || '',
      storysummary: ''
    }));
    const payload = {
      testSuiteName: fileName,
      selectedTestCaseCount: formattedData.length,
      testCaseReportDetails: formattedData,
    };
    this.downloadTCGExcelFile(payload);
  }

  openMappingModal(config: any) {
    let data = {
      displayName: "Generate Test Case - Code Mapping",
      saveBtnText: "Generate",
    };

    const modalRef = this.modalService.open(GenerateMappingModalComponent, {
      centered: true,
      size: "lg",
      backdrop: 'static',
    });

    modalRef.componentInstance.configs = {
      title: data?.displayName || "Generate Test Case - Code Mapping",
      saveBtnText: data?.saveBtnText || "Generate",
      isEdit: false,
      model: {}
    };

    // Clone the fields and set LLM field to loading state
    const fieldsWithLLM = structuredClone(generateMappingFields);
    const llmField: any = fieldsWithLLM.find((f: any) => f.id === 'llm' || f.key === 'llm');
    if (llmField) {
      llmField.options = [];
      llmField.isLoading = true;
    }

    modalRef.componentInstance.fields = fieldsWithLLM;

    // Load LLM connections after modal is opened
    this.loadLLMConnectionsForModal(modalRef);

    modalRef.result.then((result) => {
      if (result?.value) {
        // Handle the mapping generation here
        this.sendInput = config?.buildQuery(result.value);
        this.send();
      }
    }).catch(() => { });
  }

  loadLLMConnectionsForModal(modalRef: any): void {
    if (this.llmConnections.length === 0 && !this.isLLMConnectionsLoading) {
      this.isLLMConnectionsLoading = true;
      this.agenticAiService.loadLLMConnections(this.projectId).subscribe({
        next: (connections) => {
          this.llmConnections = connections.map((conn: any) => ({
            ...conn,
            value: conn.connectionName
          }));
          this.isLLMConnectionsLoading = false;

          // Update the LLM field with loaded data
          const updatedFields = structuredClone(generateMappingFields);
          const updatedLlmField: any = updatedFields.find((f: any) => f.id === 'llm' || f.key === 'llm');
          if (updatedLlmField) {
            updatedLlmField.options = this.llmConnections;
            updatedLlmField.isLoading = false;
          }
          modalRef.componentInstance.fields = updatedFields;
        },
        error: (error) => {
          console.error('Error loading LLM connections:', error);
          this.isLLMConnectionsLoading = false;

          // Update the LLM field to stop loading even on error
          const updatedFields = structuredClone(generateMappingFields);
          const updatedLlmField: any = updatedFields.find((f: any) => f.id === 'llm' || f.key === 'llm');
          if (updatedLlmField) {
            updatedLlmField.options = [];
            updatedLlmField.isLoading = false;
          }
          modalRef.componentInstance.fields = updatedFields;
        }
      });
    } else if (this.llmConnections.length > 0) {
      // If connections already loaded, update immediately
      const updatedFields = structuredClone(generateMappingFields);
      const updatedLlmField: any = updatedFields.find((f: any) => f.id === 'llm' || f.key === 'llm');
      if (updatedLlmField) {
        updatedLlmField.options = this.llmConnections;
        updatedLlmField.isLoading = false;
      }
      modalRef.componentInstance.fields = updatedFields;
    }
  }

  openMappingConfigsModal(data: any, metaData:any, isEdit?:boolean) {
    let prefilledData= metaData?.result?.[0]?.data?.prefilledData;
    const modalRef = this.modalService.open(DataDisplayModalComponent, {
      centered: true,
      size: "lg",
      windowClass: "add-item-modal",
      backdrop: 'static',
    });
  
    modalRef.componentInstance.mainFields = [
      {
        key: "mappingName",
        name: "Mapping Name",
        type: "text-input",
      },
      {
        key: "githubLink",
        name: "Git Repo Link ",
        type: "edit-text",
      },
      {
        key: "vectorDatabase",
        name: "Vector Database Name",
        type: "text-input",
      },
      {
        key: "llm",
        name: "LLM",
        type: "text-input",
        isLoading: true,
      }
    ];
  
    modalRef.componentInstance.data = { ...(isEdit ? prefilledData : data), llm: 'Loading...' };
    modalRef.componentInstance.configs = {
      title: isEdit ? "Update GitHub repository link" : "Code Config Info",
      subTitle: "",
      type: 'grid',
      isCustom: true,
      buttonText: isEdit? "Update" : "Close",
      isCancelBtn: true,
      isEdit: isEdit
    };

      // Resolve LLM connection name from store
    this.store.select(selectConnections)
      .pipe(take(1))
      .subscribe((connections: any) => {
        const llmConnections = connections?.LLM || [];
        const embeddingSourceConnectionId = isEdit? prefilledData?.embeddingSourceConnection : data?.embeddingSourceConnection;
          
        let resolvedLlmName = '-';
        if (embeddingSourceConnectionId) {
          const matchedConnection = llmConnections.find(
            (conn: any) => conn.id === embeddingSourceConnectionId
          );
            
          if (matchedConnection) {
            resolvedLlmName = matchedConnection.connectionName || matchedConnection.name || embeddingSourceConnectionId;
          } else {
            resolvedLlmName = embeddingSourceConnectionId;
          }
        }
        modalRef.componentInstance.data = { ...(isEdit ? prefilledData : data), llm: resolvedLlmName };
          // Update mainFields to remove loading state
        const updatedFields = modalRef.componentInstance.mainFields.map((field: any) => {
          if (field.key === 'llm') {
            return { ...field, isLoading: false };
          }
          return field;
        });
        modalRef.componentInstance.mainFields = updatedFields;
    });
  
    modalRef.result.then((result) => {
      if(isEdit && result){
          //update the git url
        this.sendInput =  "Identify impacted Test Cases for \nGit URL : " + result?.githubLink;
        this.send();
      }
     });
  }

  openDefectReportUploadModal(metaData:any) {
    let prefilledData= metaData?.result?.[0]?.data?.prefilledData;
    const modalRef = this.modalService.open(UploadDefectReportModalComponent, {
      centered: true,
      size: "lg",
      backdrop: 'static',
    });

    modalRef.componentInstance.configs = {
      title: "Upload defect report",
      saveBtnText: "Save",
      data: prefilledData
    };

    modalRef.result.then((result) => {
      if (result?.value) {
        let query = "Identify Most Impacted Testcases ";
        if(result?.value?.file){
          this.attachments.push(result.value.file);
        } 
        if(result?.value?.githubUrl){
          query += "\nGit repository URL: " + result.value.githubUrl;
        }
        this.sendInput = query;
        this.send();
      }
    }).catch(() => { });
  }

  exportToexcel(data:any, id:string){
    let fileName = "";
    let headerMap = {};
    const resolvedId = id.startsWith("impacted_testcases_") ? "impacted_testcases"
      : id.startsWith("most_impacted_testcases_") ? "most_impacted_testcases"
      : id;
    switch (resolvedId){
      case "most_impacted_testcases":
        headerMap = {
          userStoryId: "User Story ID",
          testCaseId: "Test Case ID",
          testcaseid: "Test Case ID",
          userStoryDescription: "User Story Description",
          acceptanceCriteria: "Acceptance Criteria",
          testScenario: "Test Scenario",
          preCondition: "Pre Condition",
          pre_condition: "Pre Condition",
          steps: "Steps",
          expectedResults: "Expected Results",
          sourceCodePath: "Source Code Path",
          sourcecodepath: "Source Code Path",
          codeSnippets: "Code Snippets",
          codesnippets: "Code Snippets",
          codeSnippetId: "Code Snippet ID",
          codesnippetid: "Code Snippet ID",
          methodName: "Method Name",
          methodname: "Method Name",
          patch: "Patch"
        }
        fileName = "most_impacted_testcases"
        break;

      case "impacted_testcases": 
        headerMap = {
          userStoryId: "User Story ID",
          testCaseId: "Test Case ID",
          testcaseid: "Test Case ID",
          userStoryDescription: "User Story Description",
          acceptanceCriteria: "Acceptance Criteria",
          testScenario: "Test Scenario",
          preCondition: "Pre Condition",
          pre_condition: "Pre Condition",
          steps: "Steps",
          expectedResults: "Expected Results",
          sourceCodePath: "Source Code Path",
          sourcecodepath: "Source Code Path",
          codeSnippets: "Code Snippets",
          codesnippets: "Code Snippets",
          codeSnippetId: "Code Snippet ID",
          codesnippetid: "Code Snippet ID",
          methodName: "Method Name",
          methodname: "Method Name",
          patch: "Patch"
        }
        fileName = "impacted_testcases"
        break;

      case "recommendations_table": 
        headerMap = {
          codeSnippetId: "Code Snippet ID",
          codeSnippets: "Code Snippets",
          class: "Class",
          sourceCodePath: "Source Code Path",
          codeFile: "Code File",
          recommendations: "Recommendations"
        }
        fileName = "recommendations"
        break;

      default :
        fileName = "download"
        break;
    }
    this.excelService.exportToExcelWithHeaderMap(data, fileName, headerMap);
  }

}